import React, {
  RefObject,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useTableReset,
} from 'core/hooks';
import {
  VirtualTableInstance,
} from 'core/components/VirtualTable/types';
import Table from 'core/components/VirtualTable';
import {
  getBillingProviders,
} from 'views/EMR/Settings/transport';
import {
  BillingProviderInfo,
} from 'views/EMR/Settings/BillingConfigurations/types';
import Section from 'views/EMR/Settings/BillingConfigurations/Section';
import {
  commonTableClasses,
} from 'views/EMR/Settings/BillingConfigurations';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import {
  settingsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import billingProviderColumns from 'views/EMR/Settings/BillingConfigurations/BillingProvider/columns';
import BillingProviderDialog, {
  BillingProviderDialogProps,
} from 'views/EMR/Settings/BillingConfigurations/BillingProvider/Dialog';
import isNil from 'lodash/isNil';

export default function BillingProvider() {
  const { tableInstanceRef } = useTableReset();
  const userInfo = useUserInfo();

  const [
    billingProviderDialogParams,
    setBillingProviderDialogParams,
  ] = useState<BillingProviderDialogProps | null>(null);

  useEffect(() => {
    const refetch = () => {
      tableInstanceRef?.current?.refetch?.();
    };
    const unsubscribeFromBillingProviderChange = settingsEventChannel.on('onBillingProviderChange', refetch);
    const unsubscribeFromBillingProviderPtanChange = settingsEventChannel.on('onBillingProviderPtanChange', refetch);
    const unsubscribeFromBillingProviderRuleChange = settingsEventChannel.on('onBillingProviderRuleChange', refetch);

    return () => {
      unsubscribeFromBillingProviderChange();
      unsubscribeFromBillingProviderPtanChange();
      unsubscribeFromBillingProviderRuleChange();
    };
  }, []);

  const columns = useMemo(() => (
    userInfo?.flags?.mayEditOrgConfiguration
      ? billingProviderColumns
      : billingProviderColumns.slice(0, -1)
  ), [userInfo]);

  const onAdd = () => {
    setBillingProviderDialogParams({
      billingProvider: null,
    });
  };

  return (
    <>
      <Section
        headerProps={{
          title: 'Billing Providers',
          onAdd: userInfo?.flags?.mayEditOrgConfiguration ? onAdd : undefined,
          onAddTooltip: 'Add Billing Provider',
        }}
      >
        <Table<BillingProviderInfo>
          instanceRef={tableInstanceRef as RefObject<VirtualTableInstance>}
          getData={getBillingProviders}
          getRowId={(row) => row?.billingProviderId}
          columns={columns}
          emptySlot="No Billing Providers Found"
          classes={{
            ...commonTableClasses,
            row: '!py-1 !px-2',
          }}
          dataKey="billing-providers"
          onRowClick={(table, row) => {
            if (userInfo?.flags?.mayEditOrgConfiguration) {
              setBillingProviderDialogParams({
                billingProvider: row?.original,
              });
            }
          }}
        />
      </Section>
      {!isNil(billingProviderDialogParams) && (
        <BillingProviderDialog
          {...billingProviderDialogParams}
          onClose={() => { setBillingProviderDialogParams(null); }}
        />
      )}
    </>
  );
}
